import { z } from 'zod';
import { API_VERSION, semverSchema } from './version';
import { minAppVersionByPlatformSchema } from './min-app-version';
import {
  assetUrlSchema,
  emailSchema,
  hexColorSchema,
  httpsUrlSchema,
  tenantCodeSchema,
} from './common';
import { manifestLogoAspectSchema, manifestLogoShapeSchema } from './logo';
import { MANIFEST_MODULE_ID_PATTERN, MANIFEST_MODULE_PERMISSION_PATTERN } from './modules';

export const paletteSchema = z.object({
  primary: hexColorSchema,
  onPrimary: hexColorSchema,
  accent: hexColorSchema,
  background: hexColorSchema,
  surface: hexColorSchema,
  text: hexColorSchema,
});
export type Palette = z.infer<typeof paletteSchema>;

export const themeSchema = z.object({ light: paletteSchema, dark: paletteSchema });
export type Theme = z.infer<typeof themeSchema>;

export const authProviderIdSchema = z.enum(['google', 'microsoft', 'emailOtp']);
export type AuthProviderId = z.infer<typeof authProviderIdSchema>;

export const authProviderSchema = z.object({
  id: authProviderIdSchema,
  label: z.string().min(1).max(60),
  kcIdpHint: z.string().min(1).nullable(),
});
export type AuthProvider = z.infer<typeof authProviderSchema>;

export const manifestModuleSchema = z.object({
  /** Core module id (`timetable`) or middleware module id (`powerschool.timetable`). */
  id: z.string().regex(MANIFEST_MODULE_ID_PATTERN),
  enabled: z.boolean(),
  title: z.string().min(1).max(60),
  icon: z.string().min(1).max(40),
  permission: z.string().regex(MANIFEST_MODULE_PERMISSION_PATTERN),
  order: z.number().int().min(0),
  config: z.record(z.string(), z.unknown()),
});
export type ManifestModule = z.infer<typeof manifestModuleSchema>;

/** GET {tenantUrl}/api/v1/manifest — public, nothing sensitive (brief §5.2). */
export const manifestSchema = z.object({
  apiVersion: z.literal(API_VERSION),
  minAppVersion: semverSchema,
  /** Per-platform minimum (additive, review S7); see effectiveMinAppVersion. */
  minAppVersionByPlatform: minAppVersionByPlatformSchema.optional(),
  tenant: z.object({
    code: tenantCodeSchema,
    name: z.string().min(1).max(120),
    shortName: z.string().min(1).max(24),
    logoUrl: assetUrlSchema,
    /** Logo width / height, when the server knows it (additive; absent = lay out as square). */
    logoAspect: manifestLogoAspectSchema,
    /** `wide` from aspect 1.6 (LOGO_WIDE_MIN_ASPECT); tall and unknown logos are `square`. */
    logoShape: manifestLogoShapeSchema,
    theme: themeSchema,
    /** Present only when the school is not `active`; lets the app show the inactive screen. */
    status: z.enum(['active', 'maintenance', 'suspended']).default('active'),
  }),
  auth: z.object({
    issuer: httpsUrlSchema,
    clientId: z.string().min(1),
    providers: z.array(authProviderSchema).min(1),
  }),
  features: z.object({
    biometricUnlockAllowed: z.boolean(),
    lockAfterBackgroundSeconds: z.number().int().min(0).max(86_400),
    pushEnabled: z.boolean(),
  }),
  modules: z.array(manifestModuleSchema),
  support: z.object({ email: emailSchema.nullable(), url: httpsUrlSchema.nullable() }),
});
export type Manifest = z.infer<typeof manifestSchema>;

export type ManifestParseResult =
  { ok: true; manifest: Manifest } | { ok: false; issues: string[] };

/** Validates a manifest without throwing; invalid → "School configuration error" screen. */
export function parseManifest(input: unknown): ManifestParseResult {
  const result = manifestSchema.safeParse(input);
  if (result.success) return { ok: true, manifest: result.data };
  return {
    ok: false,
    issues: result.error.issues.map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`),
  };
}

/**
 * Home shows registry ∩ manifest.modules(enabled) ∩ permitted, sorted by order (brief §9.7).
 * Unknown module IDs (not in the app's registry) are ignored for forward compatibility.
 */
export function visibleModules(
  modules: readonly ManifestModule[],
  registryIds: ReadonlySet<string>,
  permissions: ReadonlySet<string>,
): ManifestModule[] {
  return modules
    .filter((m) => m.enabled && registryIds.has(m.id) && permissions.has(m.permission))
    .slice()
    .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
}
